import { z } from "zod"

import {
  selfServeTrialUnlockMethods,
  subscriptionBillingPeriods,
  subscriptionPlanIds,
  subscriptionStatuses,
  trialUnlockMethods,
} from "../../billing.js"

/** Page step chosen on the pricing slider. Omitted means the plan's cheapest
 * step, which is what a caller that predates tiers sends. */
const includedPagesField = z.number().int().positive().optional()

export const ApiCreateCheckoutBodySchema = z.object({
  planId: z.enum(subscriptionPlanIds),
  period: z.enum(subscriptionBillingPeriods),
  includedPages: includedPagesField,
})

export type ApiCreateCheckoutBody = z.infer<typeof ApiCreateCheckoutBodySchema>

export const ApiChangePlanBodySchema = z.object({
  planId: z.enum(subscriptionPlanIds),
  billingPeriod: z.enum(subscriptionBillingPeriods),
  includedPages: includedPagesField,
  /**
   * The price the quote was taken against, echoed back from the preview. The
   * change refuses to apply when the subscription has moved since, so an admin
   * can never be charged an amount other than the one they were shown.
   * Optional: a caller that did not quote first simply applies the change.
   */
  expectedCurrentPriceId: z.string().optional(),
})

export type ApiChangePlanBody = z.infer<typeof ApiChangePlanBodySchema>

export const ApiChangePlanResponseSchema = z.object({
  planId: z.enum(subscriptionPlanIds),
  billingPeriod: z.enum(subscriptionBillingPeriods),
  includedPages: z.number().int().positive().nullable(),
  status: z.enum(subscriptionStatuses),
})

export type ApiChangePlanResponse = z.infer<typeof ApiChangePlanResponseSchema>

/** A plan change quoted before it is applied, so the price can be shown. */
export const ApiChangePlanPreviewResponseSchema = z.object({
  /** Collected the moment the change is confirmed. Minor units (cents). */
  dueNow: z.number().int(),
  /** The invoice after that, with any credit this change creates applied. */
  dueNextInvoice: z.number().int(),
  /** When that invoice falls. Null when Stripe has not scheduled one. */
  nextInvoiceAt: z.string().datetime().nullable(),
  currency: z.string(),
  /** True when the change bills a fresh full period, not just a difference. */
  resetsBillingCycle: z.boolean(),
  /** Pass back as `expectedCurrentPriceId` to bind the change to this quote. */
  currentPriceId: z.string(),
})

export type ApiChangePlanPreviewResponse = z.infer<
  typeof ApiChangePlanPreviewResponseSchema
>

export const ApiCheckoutSessionResponseSchema = z.object({
  /** Stripe-hosted Checkout URL to redirect the browser to. */
  url: z.string().url(),
})

export type ApiCheckoutSessionResponse = z.infer<
  typeof ApiCheckoutSessionResponseSchema
>

export const ApiBillingPortalResponseSchema = z.object({
  /** Stripe Billing Portal URL to redirect the browser to. */
  url: z.string().url(),
})

export type ApiBillingPortalResponse = z.infer<
  typeof ApiBillingPortalResponseSchema
>

export const ApiInvoiceSchema = z.object({
  id: z.string(),
  number: z.string().nullable(),
  status: z.string().nullable(),
  /** Amounts are in the currency's minor unit (cents). */
  amountDue: z.number().int(),
  amountPaid: z.number().int(),
  currency: z.string(),
  created: z.string().datetime(),
  hostedInvoiceUrl: z.string().url().nullable(),
  invoicePdf: z.string().url().nullable(),
})

export type ApiInvoice = z.infer<typeof ApiInvoiceSchema>

export const ApiInvoiceListResponseSchema = z.object({
  invoices: z.array(ApiInvoiceSchema),
})

export type ApiInvoiceListResponse = z.infer<
  typeof ApiInvoiceListResponseSchema
>

/**
 * Claiming the trial's share unlock. The method is what the user did, and the
 * API checks what it can: `team_invite` is verified against real invitation
 * rows, `social_share` is honour based. That asymmetry is deliberate and is
 * not a hole. The most a fabricated claim can win is the 200 page trial that
 * every organization used to be given unconditionally, so the floor here is
 * the product as it shipped last month.
 */
export const ApiTrialUnlockBodySchema = z.object({
  method: z.enum(selfServeTrialUnlockMethods),
})

export type ApiTrialUnlockBody = z.infer<typeof ApiTrialUnlockBodySchema>

export const ApiTrialUnlockResponseSchema = z.object({
  /** True once the trial holds the full allowance, whether this call is what
   * unlocked it or it was already unlocked. Idempotent on purpose: a second
   * click is a success, not a conflict. */
  trialUnlocked: z.boolean(),
  /** What unlocked it, which may not be the method just claimed if something
   * else got there first. */
  method: z.enum(trialUnlockMethods).nullable(),
  unlockedAt: z.string().datetime().nullable(),
  /** The allowance now in force, so the client can show the new number without
   * a second round trip to the usage endpoint. */
  pagesIncluded: z.number().int().nonnegative().nullable(),
})

export type ApiTrialUnlockResponse = z.infer<
  typeof ApiTrialUnlockResponseSchema
>
